import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import { getVar } from '@Sage/ssm-utils';

import pcmWorkletCode from '../lib/pcm-worklet.js';
// ========================================
// CONSTANTS
// ========================================
const RMS_THRESHOLD = 0.02; // ~ -34 dBFS; tune per environment
const HANGOVER_FRAMES = 12; // keep ~240ms of tail
const BAR_UPDATE_INTERVAL_MS = 16; // Update bars every 16ms for responsive feedback (~60 updates/sec)
const COMPRESSED_AUDIO_BITRATE = 64000; // 64kbps - good quality for voice
export const DEFAULT_CANVAS_HEIGHT = 60; // Default canvas height in CSS pixels

// ========================================
// BATCH CACHE - Avoid repeated atob/decoding
// ========================================
// WeakMap to cache decoded data for each batch object
// Key: batch object, Value: { sampleCount: number, int16Array: Int16Array }
const batchCache = new WeakMap();

/**
 * Get cached sample count for a batch, decoding only if not cached
 * @param {Object} batch - Batch object with audio property
 * @returns {number} Sample count
 */
const getCachedSampleCount = (batch) => {
    let cached = batchCache.get(batch);
    if (!cached) {
        const binary = atob(batch.audio);
        cached = {
            int16Array: null, // Lazy decode
            sampleCount: binary.length / 2, // 2 bytes per sample (PCM16)
        };
        batchCache.set(batch, cached);
    }
    return cached.sampleCount;
};

/**
 * Get cached Int16Array for a batch, decoding only if not cached
 * @param {Object} batch - Batch object with audio property
 * @returns {Int16Array} Decoded PCM16 data
 */
const getCachedInt16Array = (batch) => {
    let cached = batchCache.get(batch);
    if (!cached || !cached.int16Array) {
        const binary = atob(batch.audio);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) {
            bytes[i] = binary.charCodeAt(i);
        }
        const int16Array = new Int16Array(bytes.buffer);
        if (cached) {
            cached.int16Array = int16Array;
        } else {
            cached = {
                int16Array,
                sampleCount: binary.length / 2,
            };
            batchCache.set(batch, cached);
        }
    }
    return cached.int16Array;
};

/**
 * Calculate total sample count from batches using cache
 * @param {Array} batches - Array of batch objects
 * @returns {number} Total sample count
 */
const getTotalSampleCount = (batches) => {
    if (!batches || batches.length === 0) return 0;
    let total = 0;
    for (let i = 0; i < batches.length; i++) {
        total += getCachedSampleCount(batches[i]);
    }
    return total;
};

/**
 * Prepare canvas for rendering
 * Updates canvas internal dimensions to match the display size.
 *
 * NOTE: We intentionally do NOT apply devicePixelRatio scaling here because:
 * 1. The canvas has no CSS height constraint, only CSS width: 100%
 * 2. Scaling canvas.height by DPR would cause the canvas to display larger
 *    (since getBoundingClientRect returns the internal size when no CSS height is set)
 * 3. This created a feedback loop where height grew exponentially each frame
 *
 * The canvas will render at a 1:1 pixel ratio. On Retina/high-DPI displays this may
 * look slightly softer/blurrier than a fully DPR-scaled canvas, but this trade-off
 * is intentional to avoid the height feedback loop and keep waveform rendering stable.
 *
 * @param {HTMLCanvasElement} canvas - The canvas element to prepare
 * @param {number} intendedHeight - The intended display height in CSS pixels (from component props)
 * @returns {{ width: number, height: number, ctx: CanvasRenderingContext2D } | null}
 */
const prepareCanvas = (canvas, intendedHeight = DEFAULT_CANVAS_HEIGHT) => {
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const rect = canvas.getBoundingClientRect();

    // Get the display width from bounding rect (CSS width: 100% constrains it)
    const displayWidth = Math.round(rect.width);

    // Use the intendedHeight parameter to avoid feedback loop
    // (getBoundingClientRect would return canvas.height when no CSS height is set)
    const displayHeight = intendedHeight > 0 ? intendedHeight : DEFAULT_CANVAS_HEIGHT;

    // Update canvas internal size to match display size (1:1 pixel ratio)
    // Only update if changed to avoid unnecessary canvas resets
    if (canvas.width !== displayWidth || canvas.height !== displayHeight) {
        canvas.width = displayWidth;
        canvas.height = displayHeight;
    }

    return { ctx, height: displayHeight, width: displayWidth };
};

// ========================================
// NOISE HANDLING CONSTANTS
// ========================================
const NOISE_FLOOR_WINDOW_MS = 2000; // Time window for noise floor estimation (2 seconds)
const NOISE_FLOOR_PERCENTILE = 0.1; // Use 10th percentile as noise floor
const NOISE_FLOOR_MARGIN = 1.5; // Multiplier above noise floor for speech detection
const HIGH_PASS_FREQUENCY = 80; // Hz - removes low-frequency rumble (HVAC, traffic)
const SPECTRAL_SPEECH_BAND_LOW = 300; // Hz - lower bound of speech frequency band
const SPECTRAL_SPEECH_BAND_HIGH = 3400; // Hz - upper bound of speech frequency band
const ADAPTIVE_THRESHOLD_MIN = 0.01; // Minimum threshold even in quiet environments
const ADAPTIVE_THRESHOLD_MAX = 0.15; // Maximum threshold to prevent complete silence

/**
 * Check if Web Audio API is supported in the current browser
 * @returns {boolean} True if Web Audio API is supported
 */
const isWebAudioSupported = () => {
    return !!(window.AudioContext || window.webkitAudioContext);
};

// ========================================
// NOISE HANDLING UTILITIES
// ========================================

/**
 * Creates a noise floor tracker that adapts to ambient noise levels
 * Uses a sliding window of RMS values to estimate the noise floor
 * Optimized to avoid array allocation and sorting on every update
 */
class NoiseFloorTracker {
    constructor(windowMs = NOISE_FLOOR_WINDOW_MS, frameMs = 20) {
        this.maxSamples = Math.ceil(windowMs / frameMs);
        this.samples = [];
        this.sortedCache = []; // Cached sorted array to avoid allocation every frame
        this.noiseFloor = ADAPTIVE_THRESHOLD_MIN;
        this.adaptiveThreshold = RMS_THRESHOLD;
        this.updateCounter = 0;
        this.updateInterval = 5; // Only recalculate every 5 frames (~100ms)
    }

    /**
     * Add a new RMS sample and update the noise floor estimate
     * Optimized: only sorts every N frames to reduce CPU usage
     * @param {number} rms - RMS value (0-1)
     * @returns {{ noiseFloor: number, threshold: number, isSpeech: boolean }}
     */
    update(rms) {
        this.samples.push(rms);

        // Keep only the most recent samples using circular buffer approach
        if (this.samples.length > this.maxSamples) {
            this.samples.shift();
        }

        this.updateCounter++;

        // Only recalculate noise floor periodically to reduce CPU usage
        // The noise floor changes slowly, so frequent updates aren't needed
        if (this.samples.length >= 10 && this.updateCounter >= this.updateInterval) {
            this.updateCounter = 0;

            // Reuse sorted array to avoid allocation
            this.sortedCache.length = 0;
            for (let i = 0; i < this.samples.length; i++) {
                this.sortedCache.push(this.samples[i]);
            }
            this.sortedCache.sort((a, b) => a - b);

            const percentileIndex = Math.floor(this.sortedCache.length * NOISE_FLOOR_PERCENTILE);
            this.noiseFloor = this.sortedCache[percentileIndex];

            // Calculate adaptive threshold based on noise floor
            this.adaptiveThreshold = Math.min(
                ADAPTIVE_THRESHOLD_MAX,
                Math.max(ADAPTIVE_THRESHOLD_MIN, this.noiseFloor * NOISE_FLOOR_MARGIN),
            );
        }

        return {
            isSpeech: rms > this.adaptiveThreshold,
            noiseFloor: this.noiseFloor,
            threshold: this.adaptiveThreshold,
        };
    }

    /**
     * Reset the tracker (e.g., when starting a new recording)
     */
    reset() {
        this.samples = [];
        this.sortedCache = [];
        this.noiseFloor = ADAPTIVE_THRESHOLD_MIN;
        this.adaptiveThreshold = RMS_THRESHOLD;
        this.updateCounter = 0;
    }
}

/**
 * Analyze frequency spectrum to detect speech vs noise
 * Speech typically has energy concentrated in 300-3400 Hz band
 * @param {AnalyserNode} analyser - Web Audio analyser node
 * @param {number} sampleRate - Audio sample rate
 * @returns {{ speechRatio: number, isSpeechLikely: boolean }}
 */
const analyzeSpectrum = (analyser, sampleRate) => {
    const frequencyData = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteFrequencyData(frequencyData);

    const binWidth = sampleRate / analyser.fftSize;
    const lowBin = Math.floor(SPECTRAL_SPEECH_BAND_LOW / binWidth);
    const highBin = Math.min(
        Math.floor(SPECTRAL_SPEECH_BAND_HIGH / binWidth),
        frequencyData.length - 1,
    );

    let speechEnergy = 0;
    let totalEnergy = 0;

    for (let i = 0; i < frequencyData.length; i++) {
        const energy = frequencyData[i] * frequencyData[i];
        totalEnergy += energy;

        if (i >= lowBin && i <= highBin) {
            speechEnergy += energy;
        }
    }

    const speechRatio = totalEnergy > 0 ? speechEnergy / totalEnergy : 0;

    // Speech typically has > 40% of energy in the speech band
    return {
        isSpeechLikely: speechRatio > 0.4,
        speechRatio,
    };
};

/**
 * Create a high-pass filter to remove low-frequency noise
 * @param {AudioContext} ctx - Audio context
 * @param {number} frequency - Cutoff frequency in Hz
 * @returns {BiquadFilterNode}
 */
const createHighPassFilter = (ctx, frequency = HIGH_PASS_FREQUENCY) => {
    const filter = ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = frequency;
    filter.Q.value = 0.707; // Butterworth response (no resonance peak)
    return filter;
};

/**
 * Check if AudioWorklet is supported in the current browser
 * @returns {boolean} True if AudioWorklet is supported
 */
const isAudioWorkletSupported = () => {
    return isWebAudioSupported() && 'audioWorklet' in AudioContext.prototype;
};

/**
 * Get the best supported compressed audio format for the current browser
 * Prioritizes WebM (Opus) for Chrome/Edge/Firefox where WebCodecs provides fast encoding
 * Falls back to M4A (AAC) for Safari which doesn't support WebM
 * @returns {{ mimeType: string, extension: string, format: string } | null}
 */
const getBestAudioFormat = () => {
    if (typeof MediaRecorder === 'undefined') {
        return null;
    }

    const formats = [
        // WebM/Opus - Chrome, Edge, Firefox (preferred - enables fast WebCodecs encoding)
        { mimeType: 'audio/webm;codecs=opus', extension: 'webm', format: 'webm' },
        { mimeType: 'audio/webm', extension: 'webm', format: 'webm' },
        // M4A/AAC - Safari (doesn't support WebM, uses MediaRecorder)
        { mimeType: 'audio/mp4;codecs=mp4a.40.2', extension: 'm4a', format: 'm4a' },
        { mimeType: 'audio/mp4', extension: 'm4a', format: 'm4a' },
    ];

    return formats.find((f) => MediaRecorder.isTypeSupported(f.mimeType)) || null;
};

/**
 * Check if WebCodecs AudioEncoder is supported
 * @returns {boolean} True if WebCodecs is available
 */
const isWebCodecsSupported = () => {
    return (
        typeof AudioEncoder !== 'undefined' && typeof AudioEncoder.isConfigSupported === 'function'
    );
};

/**
 * Efficiently decode base64 to Uint8Array using fetch API
 * @param {string} base64 - Base64 encoded string
 * @returns {Promise<Uint8Array>}
 */
const base64ToUint8Array = async (base64) => {
    const response = await fetch(`data:application/octet-stream;base64,${base64}`);
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
};

/**
 * Efficiently convert Uint8Array to base64 using FileReader
 * @param {Uint8Array} bytes - Byte array to encode
 * @returns {Promise<string>}
 */
const uint8ArrayToBase64 = (bytes) => {
    return new Promise((resolve, reject) => {
        const blob = new Blob([bytes]);
        const reader = new FileReader();
        reader.onloadend = () => {
            const dataUrl = reader.result;
            const base64 = dataUrl.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
};

/**
 * Convert Int16Array to Float32Array efficiently
 * @param {Int16Array} int16Array - Input PCM16 samples
 * @returns {Float32Array}
 */
const int16ToFloat32 = (int16Array) => {
    const float32Array = new Float32Array(int16Array.length);
    const scale = 1 / 32768;
    for (let i = 0, len = int16Array.length; i < len; i++) {
        float32Array[i] = int16Array[i] * scale;
    }
    return float32Array;
};

/**
 * Decode and concatenate PCM16 audio batches
 * @param {Array} batches - Array of {audio: base64} objects
 * @returns {Promise<Int16Array>}
 */
const decodeBatchesToPCM16 = async (batches) => {
    const pcmChunks = await Promise.all(
        batches.map(async (batch) => {
            const bytes = await base64ToUint8Array(batch.audio);
            return new Int16Array(bytes.buffer);
        }),
    );

    const totalLength = pcmChunks.reduce((sum, arr) => sum + arr.length, 0);
    const fullAudio = new Int16Array(totalLength);
    let offset = 0;
    for (const chunk of pcmChunks) {
        fullAudio.set(chunk, offset);
        offset += chunk.length;
    }
    return fullAudio;
};

// ========================================
// WEBCODECS ENCODER (Chrome/Edge - ~100x faster)
// ========================================

/**
 * Convert PCM16 audio to Opus in WebM container using Mediabunny
 * Mediabunny handles both encoding and muxing for proper container format
 * @param {Int16Array} pcmData - PCM16 audio data
 * @param {number} sampleRate - Sample rate
 * @param {string} outputFormat - Output format identifier to return (e.g., 'webm')
 * @returns {Promise<{ base64: string, format: string, blob: Blob } | null>}
 */
const encodeWithMediabunny = async (pcmData, sampleRate, outputFormat = 'webm') => {
    let tempContext = null;

    try {
        // Dynamic import to enable tree-shaking
        const { AudioBufferSource, BufferTarget, Output, WebMOutputFormat } =
            await import('mediabunny');

        // Convert PCM16 to Float32
        const floatData = int16ToFloat32(pcmData);

        // Create an AudioBuffer from the float data
        const AC = window.AudioContext || window.webkitAudioContext;
        tempContext = new AC({ sampleRate });
        const audioBuffer = tempContext.createBuffer(1, floatData.length, sampleRate);
        audioBuffer.copyToChannel(floatData, 0);

        // Create Mediabunny output with WebM format
        const target = new BufferTarget();
        const output = new Output({
            format: new WebMOutputFormat(),
            target,
        });

        // Create audio source with Opus codec
        const audioSource = new AudioBufferSource({
            bitrate: COMPRESSED_AUDIO_BITRATE,
            codec: 'opus',
        });

        output.addAudioTrack(audioSource);
        await output.start();

        // Feed the audio buffer to the source
        await audioSource.add(audioBuffer);

        // Close the source to signal no more samples
        audioSource.close();

        // Finalize the output
        await output.finalize();

        // Get the resulting buffer
        const webmData = new Uint8Array(target.buffer);

        if (webmData.length === 0) {
            return null;
        }

        const mimeType = 'audio/webm';
        const blob = new Blob([webmData], { type: mimeType });
        const base64 = await uint8ArrayToBase64(webmData);

        return {
            base64,
            blob,
            format: outputFormat,
        };
    } catch (err) {
        console.error('Mediabunny encoding failed:', err);
        return null;
    } finally {
        // Always close the temporary AudioContext to prevent memory leak
        if (tempContext && tempContext.state !== 'closed') {
            try {
                await tempContext.close();
            } catch {
                // Ignore close errors
            }
        }
    }
};

// ========================================
// WEB WORKER ENCODER (Firefox/Safari - prevents UI blocking)
// ========================================

/**
 * Web Worker code for MediaRecorder-based encoding
 * Runs in a separate thread to prevent UI blocking
 */
const audioEncoderWorkerCode = `
// Worker-side utilities
const base64ToUint8Array = async (base64) => {
    const response = await fetch('data:application/octet-stream;base64,' + base64);
    const buffer = await response.arrayBuffer();
    return new Uint8Array(buffer);
};

const uint8ArrayToBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
        binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
};

const int16ToFloat32 = (int16Array) => {
    const float32Array = new Float32Array(int16Array.length);
    const scale = 1 / 32768;
    for (let i = 0, len = int16Array.length; i < len; i++) {
        float32Array[i] = int16Array[i] * scale;
    }
    return float32Array;
};

self.onmessage = async (e) => {
    const { pcmData, sampleRate, mimeType, bitrate, format } = e.data;

    try {
        const floatData = int16ToFloat32(new Int16Array(pcmData));
        
        const AC = self.AudioContext || self.webkitAudioContext;
        const audioContext = new AC({ sampleRate });
        
        const audioBuffer = audioContext.createBuffer(1, floatData.length, sampleRate);
        if (audioBuffer.copyToChannel) {
            audioBuffer.copyToChannel(floatData, 0);
        } else {
            audioBuffer.getChannelData(0).set(floatData);
        }
        
        const dest = audioContext.createMediaStreamDestination();
        const source = audioContext.createBufferSource();
        source.buffer = audioBuffer;
        source.connect(dest);
        
        const recorder = new MediaRecorder(dest.stream, {
            audioBitsPerSecond: bitrate,
            mimeType: mimeType,
        });
        
        const chunks = [];
        recorder.ondataavailable = (evt) => {
            if (evt.data.size > 0) {
                chunks.push(evt.data);
            }
        };
        
        recorder.onstop = async () => {
            await audioContext.close();
            
            if (chunks.length === 0) {
                self.postMessage({ success: true, result: null });
                return;
            }
            
            const blob = new Blob(chunks, { type: mimeType });
            const arrayBuffer = await blob.arrayBuffer();
            const base64 = uint8ArrayToBase64(new Uint8Array(arrayBuffer));
            
            self.postMessage({
                success: true,
                result: { base64, format, blobData: arrayBuffer }
            });
        };
        
        recorder.onerror = (evt) => {
            audioContext.close().catch(() => {});
            self.postMessage({ success: false, error: evt.error?.message || 'MediaRecorder error' });
        };
        
        recorder.start();
        source.onended = () => setTimeout(() => recorder.stop(), 100);
        source.start(0);
        
    } catch (err) {
        self.postMessage({ success: false, error: err.message });
    }
};
`;

// Cached worker blob URL
let workerBlobUrl = null;

/**
 * Get or create the worker blob URL
 * @returns {string}
 */
const getWorkerBlobUrl = () => {
    if (!workerBlobUrl) {
        const blob = new Blob([audioEncoderWorkerCode], { type: 'application/javascript' });
        workerBlobUrl = URL.createObjectURL(blob);
    }
    return workerBlobUrl;
};

// Worker encoding timeout (30 seconds should be more than enough for any audio)
const WORKER_TIMEOUT_MS = 30000;

/**
 * Encode audio using Web Worker with MediaRecorder
 * @param {Int16Array} pcmData - PCM16 audio data
 * @param {number} sampleRate - Sample rate
 * @param {{ mimeType: string, format: string }} formatInfo - Audio format info
 * @returns {Promise<{ base64: string, format: string, blob: Blob } | null>}
 */
const encodeWithWorker = (pcmData, sampleRate, formatInfo) => {
    return new Promise((resolve, reject) => {
        const worker = new Worker(getWorkerBlobUrl());
        let isResolved = false;

        // Timeout to prevent worker from hanging forever
        const timeoutId = setTimeout(() => {
            if (!isResolved) {
                isResolved = true;
                worker.terminate();
                reject(new Error('Worker encoding timeout'));
            }
        }, WORKER_TIMEOUT_MS);

        worker.onmessage = (e) => {
            if (isResolved) return;
            isResolved = true;
            clearTimeout(timeoutId);
            worker.terminate();

            if (e.data.success) {
                if (e.data.result) {
                    const { base64, blobData, format } = e.data.result;
                    const blob = new Blob([blobData], { type: formatInfo.mimeType });
                    resolve({ base64, blob, format });
                } else {
                    resolve(null);
                }
            } else {
                reject(new Error(e.data.error));
            }
        };

        worker.onerror = (err) => {
            if (isResolved) return;
            isResolved = true;
            clearTimeout(timeoutId);
            worker.terminate();
            reject(err);
        };

        // Transfer the buffer to avoid copying
        const buffer = pcmData.buffer.slice(0);
        worker.postMessage(
            {
                bitrate: COMPRESSED_AUDIO_BITRATE,
                format: formatInfo.format,
                mimeType: formatInfo.mimeType,
                pcmData: buffer,
                sampleRate,
            },
            [buffer],
        );
    });
};

/**
 * Convert PCM16 audio batches to a compressed audio format
 * Strategy:
 * - WebM format: Use Mediabunny for fast encoding with proper container format (Chrome/Edge/Firefox)
 * - M4A format: Use MediaRecorder which natively produces valid M4A containers (Safari)
 * - Fallback: Web Worker + MediaRecorder for browsers without Mediabunny support
 * @param {Array} batches - Array of {audio: base64, timestamp} objects containing PCM16 data
 * @param {number} sampleRate - Sample rate of the audio (default 44100)
 * @returns {Promise<{ base64: string, format: string, blob: Blob } | null>}
 */
const convertToCompressedAudio = async (batches, sampleRate = 44100) => {
    if (!batches?.length) {
        return null;
    }

    try {
        // Decode all batches to PCM16
        const fullAudio = await decodeBatchesToPCM16(batches);

        if (!fullAudio || fullAudio.length === 0) {
            return null;
        }

        // Detect the best supported format first
        const formatInfo = getBestAudioFormat();

        if (!formatInfo) {
            console.warn('No compressed audio format supported in this browser');
            return null;
        }

        // Use Mediabunny for WebM format (Chrome/Edge/Firefox)
        // Mediabunny produces properly containerized WebM files
        const isWebMFormat = formatInfo.format === 'webm';

        if (isWebMFormat) {
            try {
                const result = await encodeWithMediabunny(fullAudio, sampleRate, 'webm');
                if (result) {
                    return result;
                }
            } catch (err) {
                console.warn('Mediabunny encoding failed, falling back to MediaRecorder:', err);
            }
        }

        // Use MediaRecorder for M4A (Safari) or as fallback for WebM
        // MediaRecorder natively produces properly containerized audio files
        return await encodeWithWorker(fullAudio, sampleRate, formatInfo);
    } catch (err) {
        console.error('Error converting to compressed audio:', err);
        return null;
    }
};

/**
 * Default audio frame duration in milliseconds
 * This determines the size of each audio batch produced during recording
 * At 44.1kHz sample rate: 20ms = 882 samples = 1764 bytes (PCM16)
 */
export const DEFAULT_FRAME_MS = 20;

// Player status constants
const STATUS = {
    ERROR: 'error',
    IDLE: 'idle',
    RECORDING: 'recording',
    STARTING: 'starting',
    STOPPING: 'stopping',
};

// ========================================
// STATE MANAGEMENT
// ========================================
const initialState = {
    audioBufferDuration: 0, // actual audio buffer duration (VAD-filtered, no silences)
    audioDuration: 0, // recording duration including silences
    error: undefined,
    isPlaying: false,
    level: 0,
    playbackStartTime: 0,
    recordingStartTime: 0, // tracks when recording started for duration calculation
    status: STATUS.IDLE,
    waveformData: null,
};

const actionTypes = {
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_AUDIO_BUFFER_DURATION: 'SET_AUDIO_BUFFER_DURATION',
    SET_AUDIO_DURATION: 'SET_AUDIO_DURATION',
    SET_ERROR: 'SET_ERROR',
    SET_IS_PLAYING: 'SET_IS_PLAYING',
    SET_LEVEL: 'SET_LEVEL',
    SET_PLAYBACK_START_TIME: 'SET_PLAYBACK_START_TIME',
    SET_RECORDING_START_TIME: 'SET_RECORDING_START_TIME',
    SET_STATUS: 'SET_STATUS',
    SET_WAVEFORM_DATA: 'SET_WAVEFORM_DATA',
};

const audioReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.SET_STATUS:
            return { ...state, status: action.payload };
        case actionTypes.SET_ERROR:
            return { ...state, error: action.payload };
        case actionTypes.CLEAR_ERROR:
            return { ...state, error: undefined };
        case actionTypes.SET_LEVEL:
            return { ...state, level: action.payload };
        case actionTypes.SET_WAVEFORM_DATA:
            return { ...state, waveformData: action.payload };
        case actionTypes.SET_IS_PLAYING:
            return { ...state, isPlaying: action.payload };
        case actionTypes.SET_AUDIO_BUFFER_DURATION:
            return { ...state, audioBufferDuration: action.payload };
        case actionTypes.SET_AUDIO_DURATION:
            return { ...state, audioDuration: action.payload };
        case actionTypes.SET_PLAYBACK_START_TIME:
            return { ...state, playbackStartTime: action.payload };
        case actionTypes.SET_RECORDING_START_TIME:
            return { ...state, recordingStartTime: action.payload };
        default:
            return state;
    }
};

const useAudioEvents = (props = {}) => {
    const {
        batchSize = 8, // ~160ms batches before callback
        colors = {}, // customizable graph colors
        frameMs = DEFAULT_FRAME_MS,
        // Noise handling options
        noiseHandling = {}, // { adaptiveThreshold, highPassFilter, spectralVAD }
        onAudioBatch, // callback for each batch of audio
        onRecordingComplete, // callback when recording stops with all audio
        targetRate = 44100,
        waveformBars = 100, // number of bars for static waveform
        workletCode, // optional custom worklet code (defaults to bundled)
    } = props;

    // Noise handling configuration with defaults
    const noiseConfig = useMemo(
        () => ({
            // Enable adaptive threshold that adjusts to ambient noise
            adaptiveThreshold: noiseHandling.adaptiveThreshold ?? true,
            // Enable high-pass filter to remove low-frequency rumble
            highPassFilter: noiseHandling.highPassFilter ?? true,
            // Cutoff frequency for high-pass filter (Hz)
            highPassFrequency: noiseHandling.highPassFrequency ?? HIGH_PASS_FREQUENCY,
            // Enable spectral analysis for better speech detection
            spectralVAD: noiseHandling.spectralVAD ?? true,
        }),
        [
            noiseHandling.adaptiveThreshold,
            noiseHandling.highPassFilter,
            noiseHandling.highPassFrequency,
            noiseHandling.spectralVAD,
        ],
    );

    // Default colors with user overrides (sanitized for CSS vars)
    const graphColors = useMemo(
        () => ({
            centerLine: getVar(colors.centerLine || 'rgba(0, 102, 204, 0.2)'),
            waveform: getVar(colors.waveform || 'rgba(0, 102, 204, 0.6)'),
            waveformPlayed: getVar(colors.waveformPlayed || 'rgba(0, 102, 204, 1)'),
        }),
        [colors.centerLine, colors.waveform, colors.waveformPlayed],
    );

    // ========================================
    // LOCAL STATE
    // ========================================
    const [state, dispatch] = useReducer(audioReducer, initialState);
    const {
        audioBufferDuration,
        audioDuration,
        error,
        isPlaying,
        level,
        playbackStartTime,
        recordingStartTime,
        status,
        waveformData,
    } = state;

    // ========================================
    // WORKLET URL MANAGEMENT
    // ========================================
    const workletUrl = useMemo(() => {
        const code = workletCode || pcmWorkletCode;
        const blob = new Blob([code], { type: 'application/javascript' });
        return URL.createObjectURL(blob);
    }, [workletCode]);

    // Clean up Blob URL on unmount
    useEffect(() => {
        return () => {
            URL.revokeObjectURL(workletUrl);
        };
    }, [workletUrl]);

    // ========================================
    // REFS
    // ========================================
    const analyserRef = useRef(null);
    const analyserBufferRef = useRef(null); // cached Uint8Array for analyser to avoid allocation every frame
    const audioBatchesRef = useRef([]); // stores all audio batches
    const batchRef = useRef([]); // temporary batch buffer
    const canvasRef = useRef(null);
    const canvasHeightRef = useRef(DEFAULT_CANVAS_HEIGHT); // stores intended canvas height to avoid feedback loop in HiDPI
    const cachedAudioBufferRef = useRef(null); // Cached AudioBuffer for repeat playback
    const cachedBatchesHashRef = useRef(null); // Hash to detect batch changes
    const lastDrawnProgressRef = useRef(-1); // Track last drawn progress to skip unnecessary redraws
    const highPassFilterRef = useRef(null); // high-pass filter for noise reduction
    const isPlaybackModeRef = useRef(false); // track if visualizer is in playback mode
    const isSpeakingRef = useRef(false);
    const nodeRef = useRef(null);
    const noiseFloorTrackerRef = useRef(null); // adaptive noise floor tracker
    const playbackSourceRef = useRef(null); // for stopping playback
    const pausedPositionRef = useRef(0); // track where playback was paused
    const playbackStartTimeRef = useRef(0); // track playback start time for position calculation
    const rafRef = useRef(null);
    const recordingBarsRef = useRef([]); // accumulates bar amplitudes during recording
    const recordingLastUpdateRef = useRef(0); // timestamp of last bar update for throttling
    const sharedContextRef = useRef(null); // shared AudioContext for both recording and playback
    const speakCountRef = useRef(0);
    const srcRef = useRef(null);
    const streamRef = useRef(null);

    // ========================================
    // UTILITY FUNCTIONS
    // ========================================
    const b64FromArrayBuffer = useCallback((buf) => {
        const bytes = new Uint8Array(buf);
        let binary = '';
        const chunk = 0x8000;
        for (let i = 0; i < bytes.length; i += chunk) {
            binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
        }
        return btoa(binary);
    }, []);

    /**
     * Get or create shared AudioContext for both recording and playback
     * Reuses existing context if available, creates new one if needed
     * @returns {AudioContext|null} The AudioContext instance or null if not supported
     */
    const getOrCreateContext = useCallback(() => {
        if (!isWebAudioSupported()) {
            console.warn('Web Audio API is not supported in this browser');
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: 'Web Audio API is not supported in this browser',
            });
            return null;
        }

        if (
            !sharedContextRef.current ||
            sharedContextRef.current.state === 'closed' ||
            sharedContextRef.current.state === 'interrupted'
        ) {
            const AC = window.AudioContext || window.webkitAudioContext;
            sharedContextRef.current = new AC();
        }
        return sharedContextRef.current;
    }, []);

    /**
     * Generate static waveform data from audio batches
     * Returns array of normalized amplitude values (0-1)
     */
    const generateStaticWaveform = useCallback(
        (batches) => {
            if (!batches || batches.length === 0) {
                return null;
            }

            try {
                // Decode all batches to PCM16 data using cached Int16Arrays
                const pcmChunks = batches.map((batch) => getCachedInt16Array(batch));

                // Concatenate all PCM chunks
                const totalLength = pcmChunks.reduce((sum, arr) => sum + arr.length, 0);
                const fullAudio = new Int16Array(totalLength);
                let offset = 0;
                for (const chunk of pcmChunks) {
                    fullAudio.set(chunk, offset);
                    offset += chunk.length;
                }

                // Calculate samples per bar - distribute all samples evenly across bars
                // This ensures the waveform represents the full audio duration
                const samplesPerBar = fullAudio.length / waveformBars;
                const waveform = [];
                let maxAmplitude = 0;

                // Calculate average RMS amplitude for each bar
                // Use floating point division to ensure all samples are included
                for (let i = 0; i < waveformBars; i++) {
                    const start = Math.floor(i * samplesPerBar);
                    const end =
                        i === waveformBars - 1
                            ? fullAudio.length // Last bar includes all remaining samples
                            : Math.floor((i + 1) * samplesPerBar);

                    if (start >= fullAudio.length) break;

                    let sum = 0;
                    const sampleCount = end - start;

                    for (let j = start; j < end; j++) {
                        const normalized = fullAudio[j] / 32768.0;
                        sum += normalized * normalized;
                    }

                    const rms = sampleCount > 0 ? Math.sqrt(sum / sampleCount) : 0;
                    waveform.push(rms);
                    maxAmplitude = Math.max(maxAmplitude, rms);
                }

                // Normalize to 0-1 range
                if (maxAmplitude > 0) {
                    for (let i = 0; i < waveform.length; i++) {
                        waveform[i] = waveform[i] / maxAmplitude;
                    }
                }

                return waveform;
            } catch (err) {
                console.error('Error generating waveform:', err);
                return null;
            }
        },
        [waveformBars],
    );

    // ========================================
    // VISUALIZER FUNCTIONS
    // ========================================
    const startViz = useCallback(
        (analyser) => {
            analyserRef.current = analyser;
            recordingLastUpdateRef.current = Date.now();

            // Pre-allocate buffer for analyser data to avoid allocation every frame
            analyserBufferRef.current = new Uint8Array(analyser.fftSize);

            const draw = () => {
                if (!analyserRef.current) return;
                const a = analyserRef.current;

                // Reuse cached buffer instead of allocating new one every frame
                let td = analyserBufferRef.current;
                if (!td || td.length !== a.fftSize) {
                    analyserBufferRef.current = new Uint8Array(a.fftSize);
                    td = analyserBufferRef.current;
                }
                a.getByteTimeDomainData(td);

                // Calculate RMS (0..1)
                let sum = 0;
                const len = td.length;
                for (let i = 0; i < len; i++) {
                    const v = (td[i] - 128) / 128;
                    sum += v * v;
                }
                const rms = Math.sqrt(sum / len);
                dispatch({
                    type: actionTypes.SET_LEVEL,
                    payload: Math.min(1, rms),
                });

                // Throttle bar updates for slower scrolling
                const now = Date.now();
                const timeSinceLastUpdate = now - recordingLastUpdateRef.current;

                if (timeSinceLastUpdate >= BAR_UPDATE_INTERVAL_MS) {
                    // Apply silence threshold: if below RMS_THRESHOLD, treat as silence (0)
                    const amplitudeValue = rms >= RMS_THRESHOLD ? rms : 0;

                    // Scrolling bars: add new bar on right, remove from left when at capacity
                    recordingBarsRef.current.push(amplitudeValue);
                    if (recordingBarsRef.current.length > waveformBars) {
                        recordingBarsRef.current.shift(); // remove leftmost bar
                    }

                    recordingLastUpdateRef.current = now;
                }

                // Draw vertical bars (scrolling effect)
                const canvas = canvasRef.current;
                const prepared = prepareCanvas(canvas, canvasHeightRef.current);
                if (prepared) {
                    const { ctx: g, width: w, height: h } = prepared;

                    // Clear canvas (use full internal size for clearing)
                    g.clearRect(0, 0, w, h);

                    // Draw horizontal center line
                    g.strokeStyle = graphColors.centerLine;
                    g.lineWidth = 1;
                    g.beginPath();
                    g.moveTo(0, h / 2);
                    g.lineTo(w, h / 2);
                    g.stroke();

                    const bars = recordingBarsRef.current;
                    const barCount = Math.min(bars.length, waveformBars);

                    if (barCount > 0) {
                        // Calculate bar dimensions based on fixed bar count
                        const barGap = 2; // pixels between bars
                        const totalGapWidth = barGap * (waveformBars - 1);
                        const barWidth = Math.max(2, (w - totalGapWidth) / waveformBars);
                        const maxBarHeight = h * 0.9; // leave 10% padding
                        const minBarHeight = 2; // minimum height for visibility

                        // Find max amplitude in current window for normalization
                        const maxAmplitude = Math.max(...bars, 0.01);

                        // Calculate starting x position to right-align bars when not full
                        // This ensures new audio appears immediately on the right edge
                        const emptyBars = waveformBars - barCount;
                        const startX = emptyBars * (barWidth + barGap);

                        // Draw bars (scrolling from right to left, right-aligned)
                        for (let i = 0; i < barCount; i++) {
                            const normalizedAmp = bars[i] / maxAmplitude;
                            const barHeight = Math.max(minBarHeight, normalizedAmp * maxBarHeight);
                            const x = startX + i * (barWidth + barGap);
                            const y = (h - barHeight) / 2; // center vertically

                            g.fillStyle = graphColors.waveform;

                            // Draw rounded rectangle for each bar
                            const radius = Math.min(barWidth / 2, 2);
                            g.beginPath();
                            g.roundRect(x, y, barWidth, barHeight, radius);
                            g.fill();
                        }
                    }
                }
                rafRef.current = requestAnimationFrame(draw);
            };
            rafRef.current = requestAnimationFrame(draw);
        },
        [graphColors, waveformBars],
    );

    const stopViz = useCallback(() => {
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        analyserRef.current && analyserRef.current.disconnect();
        analyserRef.current = null;
        analyserBufferRef.current = null; // Free the cached buffer
        dispatch({ type: actionTypes.SET_LEVEL, payload: 0 });
    }, []);

    const bindCanvas = useCallback((el, height = DEFAULT_CANVAS_HEIGHT) => {
        canvasRef.current = el || null;
        canvasHeightRef.current = height;
    }, []);

    /**
     * Draw static waveform as vertical bars (WhatsApp/Telegram style)
     * @param {Array} waveform - Array of normalized amplitude values
     * @param {number} currentTime - Current playback time in seconds
     * @param {number} totalDuration - Total audio duration in seconds
     * @param {number} horizontalPadding - Horizontal padding in pixels (default: 0)
     */
    const drawStaticWaveform = useCallback(
        (waveform, currentTime = 0, totalDuration = 0, horizontalPadding = 0) => {
            const canvas = canvasRef.current;
            if (!canvas || !waveform || waveform.length === 0) return;

            const prepared = prepareCanvas(canvas, canvasHeightRef.current);
            if (!prepared) return;

            const { ctx: g, width: w, height: h } = prepared;

            // Clear canvas
            g.clearRect(0, 0, w, h);

            // Draw horizontal center line
            g.strokeStyle = graphColors.centerLine;
            g.lineWidth = 1;
            g.beginPath();
            g.moveTo(0, h / 2);
            g.lineTo(w, h / 2);
            g.stroke();

            // Calculate bar dimensions with padding
            const barCount = waveform.length;
            const barGap = 2; // pixels between bars
            const availableWidth = w - horizontalPadding * 2;
            const totalGapWidth = barGap * (barCount - 1);
            const barWidth = Math.max(2, (availableWidth - totalGapWidth) / barCount);
            const maxBarHeight = h * 0.9; // leave 10% padding
            const minBarHeight = 2; // minimum height for visibility

            // Calculate progress (0 to 1) for determining which bars have been played
            const progress = totalDuration > 0 ? currentTime / totalDuration : 0;
            const playedBarCount = Math.floor(progress * barCount);

            // Draw bars with horizontal padding offset
            for (let i = 0; i < barCount; i++) {
                const barHeight = Math.max(minBarHeight, waveform[i] * maxBarHeight);
                const x = horizontalPadding + i * (barWidth + barGap);
                const y = (h - barHeight) / 2; // center vertically

                // Use played color for bars that have been played, normal color for the rest
                g.fillStyle =
                    i < playedBarCount ? graphColors.waveformPlayed : graphColors.waveform;

                // Draw rounded rectangle for each bar
                const radius = Math.min(barWidth / 2, 2);
                g.beginPath();
                g.roundRect(x, y, barWidth, barHeight, radius);
                g.fill();
            }
        },
        [graphColors.centerLine, graphColors.waveform, graphColors.waveformPlayed],
    );

    // ========================================
    // EFFECTS
    // ========================================
    // Clean up shared AudioContext and audio batches on unmount
    useEffect(() => {
        return () => {
            // Stop any ongoing playback
            if (playbackSourceRef.current) {
                try {
                    playbackSourceRef.current.stop();
                } catch (err) {
                    // Ignore errors if already stopped
                }
                playbackSourceRef.current = null;
            }

            // Stop visualization
            if (rafRef.current) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }

            // Disconnect audio nodes
            if (nodeRef.current) {
                nodeRef.current.disconnect();
                nodeRef.current = null;
            }
            if (highPassFilterRef.current) {
                highPassFilterRef.current.disconnect();
                highPassFilterRef.current = null;
            }
            if (srcRef.current) {
                srcRef.current.disconnect();
                srcRef.current = null;
            }
            if (analyserRef.current) {
                analyserRef.current.disconnect();
                analyserRef.current = null;
            }

            // Reset noise floor tracker
            if (noiseFloorTrackerRef.current) {
                noiseFloorTrackerRef.current.reset();
            }

            // Stop media stream tracks
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((t) => t.stop());
                streamRef.current = null;
            }

            // Close shared AudioContext
            if (sharedContextRef.current && sharedContextRef.current.state !== 'closed') {
                sharedContextRef.current.close().catch(() => {
                    // Intentionally ignore close errors
                });
                sharedContextRef.current = null;
            }

            // Clear audio batches and cached data to free memory
            audioBatchesRef.current = [];
            batchRef.current = [];
            recordingBarsRef.current = [];
            cachedAudioBufferRef.current = null;
            cachedBatchesHashRef.current = null;

            // Reset paused position on unmount
            pausedPositionRef.current = 0;
            playbackStartTimeRef.current = 0;
        };
    }, []);

    // Draw static waveform when data is available and not actively recording
    useEffect(() => {
        if (waveformData && status === STATUS.IDLE && waveformData.length > 0) {
            // Use audioBufferDuration if available (for playback), otherwise use audioDuration
            // Only draw if duration is greater than 0 to ensure accurate visualization
            const duration = audioBufferDuration || audioDuration;
            if (duration > 0) {
                // Use requestAnimationFrame to ensure canvas is ready and has proper dimensions
                requestAnimationFrame(() => {
                    const canvas = canvasRef.current;
                    if (canvas && canvas.width > 0 && canvas.height > 0) {
                        drawStaticWaveform(waveformData, 0, duration, 0);
                    }
                });
            }
        }
    }, [audioBufferDuration, audioDuration, drawStaticWaveform, status, waveformData]);

    // ==========================================================
    // VAD (Voice Activity Detection) GATING & BATCH PROCESSING
    // ==========================================================
    const flushBatchIfNeeded = useCallback(
        (force) => {
            if (force || batchRef.current.length >= batchSize) {
                const batch = batchRef.current.splice(0, batchRef.current.length);
                if (batch.length > 0) {
                    // Store in accumulated batches
                    audioBatchesRef.current.push(...batch);
                    // Call callback if provided
                    if (onAudioBatch) {
                        onAudioBatch(batch);
                    }
                }
            }
        },
        [batchSize, onAudioBatch],
    );

    // ========================================
    // EVENT HANDLERS & CALLBACKS
    // ========================================
    const start = useCallback(() => {
        // Check for Web Audio API and AudioWorklet support before starting
        if (!isWebAudioSupported()) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: 'Web Audio API is not supported in this browser',
            });
            dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            return Promise.reject(new Error('Web Audio API is not supported'));
        }

        if (!isAudioWorkletSupported()) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: 'AudioWorklet is not supported in this browser',
            });
            dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            return Promise.reject(new Error('AudioWorklet is not supported'));
        }

        dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.STARTING });
        dispatch({ type: actionTypes.CLEAR_ERROR });
        dispatch({ type: actionTypes.SET_WAVEFORM_DATA, payload: null });
        dispatch({ type: actionTypes.SET_AUDIO_DURATION, payload: 0 });
        dispatch({ type: actionTypes.SET_AUDIO_BUFFER_DURATION, payload: 0 });

        // Clear previous audio batches and playback mode
        audioBatchesRef.current = [];
        batchRef.current = [];
        isPlaybackModeRef.current = false;
        pausedPositionRef.current = 0; // Reset paused position for new recording
        playbackStartTimeRef.current = 0; // Reset playback start time for new recording
        lastDrawnProgressRef.current = -1; // Reset waveform progress tracking
        cachedAudioBufferRef.current = null; // Clear cached AudioBuffer
        cachedBatchesHashRef.current = null;

        // Initialize noise floor tracker for adaptive threshold
        if (noiseConfig.adaptiveThreshold) {
            noiseFloorTrackerRef.current = new NoiseFloorTracker(NOISE_FLOOR_WINDOW_MS, frameMs);
        } else {
            noiseFloorTrackerRef.current = null;
        }

        // Start with empty bars array - bars will grow from right as audio comes in
        // This provides immediate visual feedback without the delay of pre-filled zeros
        recordingBarsRef.current = [];

        return navigator.mediaDevices
            .getUserMedia({
                audio: {
                    channelCount: 1,
                    echoCancellation: true,
                    // Enable browser's noise suppression (helps but not always sufficient)
                    noiseSuppression: true,
                    // Auto gain control helps normalize volume levels
                    autoGainControl: true,
                },
                video: false,
            })
            .then((stream) => {
                streamRef.current = stream;

                // Use shared context
                const ctx = getOrCreateContext();

                // Check if context was created successfully
                if (!ctx) {
                    throw new Error('Failed to create AudioContext');
                }

                return ctx.audioWorklet.addModule(workletUrl).then(() => ({ ctx, stream }));
            })
            .then(({ ctx, stream }) => {
                const src = ctx.createMediaStreamSource(stream);
                srcRef.current = src;

                // Create high-pass filter to remove low-frequency noise (HVAC, traffic rumble)
                let filterOutput = src;
                if (noiseConfig.highPassFilter) {
                    const highPass = createHighPassFilter(ctx, noiseConfig.highPassFrequency);
                    highPassFilterRef.current = highPass;
                    src.connect(highPass);
                    filterOutput = highPass;
                } else {
                    highPassFilterRef.current = null;
                }

                // Create worklet node for PCM16 downsampling
                const node = new AudioWorkletNode(ctx, 'pcm16-downsampler', {
                    processorOptions: { frameMs, targetRate },
                });
                nodeRef.current = node;

                // Create analyser for visualization and spectral VAD
                // Use smaller fftSize for lower latency (fftSize/sampleRate = latency)
                // At 44.1kHz: 512/44100 ≈ 12ms vs default 2048/44100 ≈ 46ms
                // Actual latency varies with device sample rate (commonly 44.1kHz, 48kHz, or 96kHz)
                // Use lower smoothingTimeConstant for faster response to audio changes
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = 0.3;

                // Audio chain with optional high-pass filter:
                // MediaStreamSource ──→ [HighPassFilter] ──┬──→ AudioWorkletNode (processing)
                //                                         └──→ AnalyserNode (visualization + spectral VAD)
                filterOutput.connect(node);
                filterOutput.connect(analyser);

                // Start visualization
                startViz(analyser);

                node.port.onmessage = (e) => {
                    // Handle error messages from worklet
                    if (e.data && typeof e.data === 'object' && e.data.type === 'error') {
                        console.error('[AudioWorklet]', e.data.message, e.data);
                        dispatch({
                            type: actionTypes.SET_ERROR,
                            payload: `Worklet error: ${e.data.message}`,
                        });
                        return;
                    }

                    const ab = e.data; // DEFAULT_FRAME_MS @ 44.1k: 1764 bytes (ArrayBuffer)

                    // Calculate RMS for VAD
                    const i16 = new Int16Array(ab);
                    let sum = 0;
                    for (let i = 0; i < i16.length; i++) {
                        const v = i16[i] / 32768;
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / i16.length);

                    // Determine if this is speech using multiple methods
                    let isSpeech = rms >= RMS_THRESHOLD; // Default: simple threshold

                    // Use adaptive threshold if enabled
                    if (noiseConfig.adaptiveThreshold && noiseFloorTrackerRef.current) {
                        const noiseResult = noiseFloorTrackerRef.current.update(rms);
                        isSpeech = noiseResult.isSpeech;
                    }

                    // Use spectral VAD for additional verification in noisy environments
                    if (noiseConfig.spectralVAD && analyserRef.current && isSpeech) {
                        const spectralResult = analyzeSpectrum(analyserRef.current, ctx.sampleRate);
                        // Require both RMS threshold AND speech-like spectrum
                        isSpeech = isSpeech && spectralResult.isSpeechLikely;
                    }

                    const above = isSpeech;

                    if (above) {
                        speakCountRef.current = HANGOVER_FRAMES;
                        if (!isSpeakingRef.current) isSpeakingRef.current = true;
                    } else if (speakCountRef.current > 0) {
                        speakCountRef.current -= 1;
                    } else {
                        isSpeakingRef.current = false;
                    }
                    if (!isSpeakingRef.current) return;

                    // Encode and enqueue audio
                    const b64 = b64FromArrayBuffer(ab);
                    batchRef.current.push({
                        audio: b64,
                        timestamp: Date.now(),
                    });

                    // Low-latency batching
                    flushBatchIfNeeded(false);

                    // Update duration from actual audio batches (real-time)
                    // Uses cached sample counts to avoid repeated atob calls
                    const allBatches = [...audioBatchesRef.current, ...batchRef.current];
                    if (allBatches.length > 0) {
                        const sampleCount = getTotalSampleCount(allBatches);
                        const duration = sampleCount / targetRate;
                        dispatch({ type: actionTypes.SET_AUDIO_DURATION, payload: duration });
                    }
                };

                // Graph is already connected above in linear fashion
                // Store recording start time for duration calculation
                dispatch({ type: actionTypes.SET_RECORDING_START_TIME, payload: Date.now() });
                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.RECORDING });
            })
            .catch((err) => {
                console.error(err);
                dispatch({
                    type: actionTypes.SET_ERROR,
                    payload: err && err.message ? err.message : String(err),
                });
                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            });
    }, [
        b64FromArrayBuffer,
        flushBatchIfNeeded,
        frameMs,
        getOrCreateContext,
        noiseConfig.adaptiveThreshold,
        noiseConfig.highPassFilter,
        noiseConfig.highPassFrequency,
        noiseConfig.spectralVAD,
        startViz,
        targetRate,
        workletUrl,
    ]);

    const resume = useCallback(() => {
        // Check for Web Audio API and AudioWorklet support before resuming
        if (!isWebAudioSupported()) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: 'Web Audio API is not supported in this browser',
            });
            dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            return Promise.reject(new Error('Web Audio API is not supported'));
        }

        if (!isAudioWorkletSupported()) {
            dispatch({
                type: actionTypes.SET_ERROR,
                payload: 'AudioWorklet is not supported in this browser',
            });
            dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            return Promise.reject(new Error('AudioWorklet is not supported'));
        }

        dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.STARTING });
        dispatch({ type: actionTypes.CLEAR_ERROR });
        dispatch({ type: actionTypes.SET_WAVEFORM_DATA, payload: null });

        // Don't clear previous audio batches - we're continuing the recording
        // Keep existing batches and append new ones
        // Only clear the temporary batch buffer
        batchRef.current = [];
        isPlaybackModeRef.current = false;
        pausedPositionRef.current = 0; // Reset paused position for new recording
        playbackStartTimeRef.current = 0; // Reset playback start time for new recording
        lastDrawnProgressRef.current = -1; // Reset waveform progress tracking

        // Don't reset accumulated duration - we're continuing the recording
        // Keep the previous duration and add to it when we stop again

        // Start with empty bars array - bars will grow from right as audio comes in
        // This provides immediate visual feedback without the delay of pre-filled zeros
        recordingBarsRef.current = [];

        return navigator.mediaDevices
            .getUserMedia({
                audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
                video: false,
            })
            .then((stream) => {
                streamRef.current = stream;

                // Use shared context
                const ctx = getOrCreateContext();

                // Check if context was created successfully
                if (!ctx) {
                    throw new Error('Failed to create AudioContext');
                }

                return ctx.audioWorklet.addModule(workletUrl).then(() => ({ ctx, stream }));
            })
            .then(({ ctx, stream }) => {
                const src = ctx.createMediaStreamSource(stream);
                srcRef.current = src;

                // Create worklet node for PCM16 downsampling
                const node = new AudioWorkletNode(ctx, 'pcm16-downsampler', {
                    processorOptions: { targetRate, frameMs },
                });
                nodeRef.current = node;

                // Create analyser for visualization
                // Use smaller fftSize for lower latency (fftSize/sampleRate = latency)
                // At 44.1kHz: 512/44100 ≈ 12ms vs default 2048/44100 ≈ 46ms
                // Actual latency varies with device sample rate (commonly 44.1kHz, 48kHz, or 96kHz)
                // Use lower smoothingTimeConstant for faster response to audio changes
                const analyser = ctx.createAnalyser();
                analyser.fftSize = 512;
                analyser.smoothingTimeConstant = 0.3;

                // Linear graph with analyser tapping source:
                // MediaStreamSource ──┬──→ AudioWorkletNode (processing)
                //                     └──→ AnalyserNode (visualization)
                src.connect(node);
                src.connect(analyser);

                // Start visualization
                startViz(analyser);

                node.port.onmessage = (e) => {
                    // Handle error messages from worklet
                    if (e.data && typeof e.data === 'object' && e.data.type === 'error') {
                        console.error('[AudioWorklet]', e.data.message, e.data);
                        dispatch({
                            type: actionTypes.SET_ERROR,
                            payload: `Worklet error: ${e.data.message}`,
                        });
                        return;
                    }

                    const ab = e.data; // DEFAULT_FRAME_MS @ 44.1k: 1764 bytes (ArrayBuffer)
                    // VAD-ish gate
                    const i16 = new Int16Array(ab);
                    let sum = 0;
                    for (let i = 0; i < i16.length; i++) {
                        const v = i16[i] / 32768;
                        sum += v * v;
                    }
                    const rms = Math.sqrt(sum / i16.length);
                    const above = rms >= RMS_THRESHOLD;

                    if (above) {
                        speakCountRef.current = HANGOVER_FRAMES;
                        if (!isSpeakingRef.current) isSpeakingRef.current = true;
                    } else if (speakCountRef.current > 0) {
                        speakCountRef.current -= 1;
                    } else {
                        isSpeakingRef.current = false;
                    }
                    if (!isSpeakingRef.current) return;

                    // Encode and enqueue audio
                    const b64 = b64FromArrayBuffer(ab);
                    batchRef.current.push({
                        audio: b64,
                        timestamp: Date.now(),
                    });

                    // Low-latency batching
                    flushBatchIfNeeded(false);

                    // Update duration from actual audio batches (real-time)
                    // Uses cached sample counts to avoid repeated atob calls
                    const allBatches = [...audioBatchesRef.current, ...batchRef.current];
                    if (allBatches.length > 0) {
                        const sampleCount = getTotalSampleCount(allBatches);
                        const duration = sampleCount / targetRate;
                        dispatch({ type: actionTypes.SET_AUDIO_DURATION, payload: duration });
                    }
                };

                // Graph is already connected above in linear fashion
                // Store recording start time for duration calculation
                dispatch({ type: actionTypes.SET_RECORDING_START_TIME, payload: Date.now() });
                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.RECORDING });
            })
            .catch((err) => {
                console.error(err);
                dispatch({
                    type: actionTypes.SET_ERROR,
                    payload: err && err.message ? err.message : String(err),
                });
                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            });
    }, [
        b64FromArrayBuffer,
        flushBatchIfNeeded,
        frameMs,
        getOrCreateContext,
        startViz,
        targetRate,
        workletUrl,
    ]);

    const stop = useCallback(() => {
        dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.STOPPING });

        // stop drawing first
        stopViz();

        // tear down audio graph nodes
        nodeRef.current && nodeRef.current.disconnect();
        nodeRef.current = null;
        srcRef.current && srcRef.current.disconnect();
        srcRef.current = null;

        // Stop media stream tracks
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }
        streamRef.current = null;

        // Don't close shared context - it may be reused for playback
        // Just let it idle
        return Promise.resolve()
            .then(() => {
                // Flush any remaining audio batches
                flushBatchIfNeeded(true);

                // Get all accumulated audio
                const allAudio = audioBatchesRef.current;

                // Use recording bars as waveform data (preserves timing including silences)
                // This ensures playback visualization matches recording visualization
                const waveform =
                    recordingBarsRef.current.length > 0
                        ? [...recordingBarsRef.current]
                        : generateStaticWaveform(allAudio);
                dispatch({ type: actionTypes.SET_WAVEFORM_DATA, payload: waveform });

                // Calculate actual recording duration from audio batches (actual recorded audio length)
                // This excludes silences and only counts actual audio data
                // Uses cached sample counts to avoid repeated atob calls
                const sampleCount = getTotalSampleCount(allAudio);
                const duration = sampleCount / targetRate;
                dispatch({ type: actionTypes.SET_AUDIO_DURATION, payload: duration });

                // Call completion callback if provided
                if (onRecordingComplete) {
                    onRecordingComplete(allAudio);
                }

                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.IDLE });
            })
            .catch((err) => {
                console.error(err);
                dispatch({
                    type: actionTypes.SET_ERROR,
                    payload: err && err.message ? err.message : String(err),
                });
                dispatch({ type: actionTypes.SET_STATUS, payload: STATUS.ERROR });
            });
    }, [flushBatchIfNeeded, generateStaticWaveform, onRecordingComplete, stopViz, targetRate]);

    // ========================================
    // PLAYBACK FUNCTIONS
    // ========================================
    /**
     * Initialize waveform visualization for playback mode
     * Call this to pre-generate and display the waveform before playing
     * @param {Array} batches - Audio batches to play
     * @param {Array} [customWaveform] - Optional pre-generated waveform (from recording) - IGNORED, always generates from batches
     * @param {number} [customDuration] - Optional duration (from recording with silences) - IGNORED, always calculates from batches
     */
    const initializeWaveform = useCallback(
        (batches, customWaveform = null, customDuration = null) => {
            if (!batches || batches.length === 0) return;

            // Store batches for later playback
            audioBatchesRef.current = batches;

            // Always generate waveform from actual audio batches (VAD-filtered) to match audio buffer
            // Don't use customWaveform from recording bars because it includes silences
            // that don't exist in the audio buffer
            const waveform = generateStaticWaveform(batches);

            // Always calculate duration from audio batches to match audio buffer duration
            // Don't use customDuration from recording because it includes silences
            // Uses cached sample counts to avoid repeated atob calls
            const sampleCount = getTotalSampleCount(batches);
            const duration = sampleCount / targetRate;

            // Set duration first, then waveform, so the effect that draws the waveform
            // will have the correct duration available
            dispatch({ type: actionTypes.SET_AUDIO_DURATION, payload: duration });
            dispatch({ type: actionTypes.SET_AUDIO_BUFFER_DURATION, payload: duration });

            if (waveform) {
                dispatch({ type: actionTypes.SET_WAVEFORM_DATA, payload: waveform });
                // Draw waveform immediately with correct duration to ensure accurate visualization
                // Use requestAnimationFrame to ensure canvas is ready and has proper width
                requestAnimationFrame(() => {
                    const canvas = canvasRef.current;
                    if (
                        canvas &&
                        waveform.length > 0 &&
                        duration > 0 &&
                        canvas.width > 0 &&
                        canvas.height > 0
                    ) {
                        drawStaticWaveform(waveform, 0, duration, 0);
                    }
                });
            }
        },
        [drawStaticWaveform, generateStaticWaveform, targetRate],
    );

    const playback = useCallback(
        async (batches = null) => {
            try {
                // Store batches if provided (for VoiceMessage component)
                // Reset paused position only if batches changed AND we're not resuming from pause
                if (batches) {
                    // Only compare batches if we don't have a paused position (not resuming)
                    // If pausedPositionRef > 0, we're resuming - preserve paused position regardless of batch comparison
                    const shouldCheckBatches = pausedPositionRef.current === 0;
                    const batchesChanged = shouldCheckBatches
                        ? audioBatchesRef.current.length !== batches.length ||
                          (audioBatchesRef.current.length > 0 &&
                              audioBatchesRef.current[0]?.audio !== batches[0]?.audio)
                        : false;

                    // Reset paused position and progress tracking only if batches changed AND we're not resuming
                    if (batchesChanged) {
                        pausedPositionRef.current = 0;
                        lastDrawnProgressRef.current = -1;
                    }
                    audioBatchesRef.current = batches;
                }

                // Use provided batches or all stored batches
                const audioToPlay = batches || audioBatchesRef.current;

                if (!audioToPlay || audioToPlay.length === 0) {
                    console.warn('No audio batches to play');
                    return;
                }

                // Stop any ongoing playback and capture position before stopping
                // This ensures we preserve the paused position even if playback() is called rapidly
                if (
                    playbackSourceRef.current &&
                    playbackStartTimeRef.current > 0 &&
                    sharedContextRef.current
                ) {
                    const audioContext = sharedContextRef.current;
                    const currentPosition = audioContext.currentTime - playbackStartTimeRef.current;
                    // Use audioBufferDuration for playback timing (actual audio length)
                    const playbackDuration = audioBufferDuration || audioDuration;
                    // Only capture paused position if we're not at the end
                    if (currentPosition < playbackDuration && playbackDuration > 0) {
                        pausedPositionRef.current = Math.max(
                            0,
                            Math.min(currentPosition, playbackDuration),
                        );
                    }
                }

                if (playbackSourceRef.current) {
                    try {
                        playbackSourceRef.current.stop();
                    } catch (err) {
                        // Ignore errors if already stopped
                    }
                    playbackSourceRef.current = null;
                }

                // Reset paused position ONLY if audio ended naturally (not in playback mode AND pausedPosition is 0)
                // If pausedPositionRef > 0, we're resuming from manual pause - keep it
                // If pausedPositionRef === 0 AND not in playback mode, audio ended naturally - keep it at 0
                // Don't reset if pausedPositionRef > 0 (manual pause)
                // The pausedPositionRef is already correct:
                //   - If 0: either never played or ended naturally
                //   - If > 0: paused manually, resume from there

                // Set playback mode
                isPlaybackModeRef.current = true;

                // Use shared AudioContext for playback
                const audioContext = getOrCreateContext();

                // Check if context was created successfully
                if (!audioContext) {
                    throw new Error('Failed to create AudioContext');
                }

                // Resume AudioContext if suspended (required for Chrome autoplay policy)
                // This ensures currentTime starts increasing immediately
                if (audioContext.state === 'suspended') {
                    await audioContext.resume();
                }

                // Create a simple hash to detect batch changes (first batch + length)
                const batchesHash =
                    audioToPlay.length > 0
                        ? `${audioToPlay.length}_${audioToPlay[0]?.audio?.slice(0, 20) || ''}`
                        : '';

                // Check if we can reuse cached AudioBuffer
                let audioBuffer = cachedAudioBufferRef.current;
                const canReuseBuffer =
                    audioBuffer &&
                    cachedBatchesHashRef.current === batchesHash &&
                    audioBuffer.sampleRate === targetRate;

                if (!canReuseBuffer) {
                    // Decode base64 to PCM16 data using cached Int16Arrays
                    const pcmData = audioToPlay.map((batch) => getCachedInt16Array(batch));

                    // Concatenate all PCM chunks
                    const totalLength = pcmData.reduce((sum, arr) => sum + arr.length, 0);
                    const fullAudio = new Int16Array(totalLength);
                    let offset = 0;
                    for (const chunk of pcmData) {
                        fullAudio.set(chunk, offset);
                        offset += chunk.length;
                    }

                    // Create new AudioBuffer
                    audioBuffer = audioContext.createBuffer(1, fullAudio.length, targetRate);

                    // Convert Int16 to Float32 for Web Audio API
                    const channelData = audioBuffer.getChannelData(0);
                    for (let i = 0; i < fullAudio.length; i++) {
                        channelData[i] = fullAudio[i] / 32768.0;
                    }

                    // Cache the buffer
                    cachedAudioBufferRef.current = audioBuffer;
                    cachedBatchesHashRef.current = batchesHash;
                }

                // Use the actual audioBuffer.duration for the most accurate duration
                // This is the exact duration that Web Audio API will use for playback
                // It accounts for any sample rate adjustments or rounding
                const bufferDuration = audioBuffer.duration;
                dispatch({ type: actionTypes.SET_AUDIO_BUFFER_DURATION, payload: bufferDuration });

                // Generate waveform from actual audio batches (VAD-filtered) for playback
                // This ensures waveform visualization matches the actual audio buffer duration
                // We don't use waveformData from recording bars because it includes silences
                // that don't exist in the audio buffer
                const waveform = generateStaticWaveform(audioToPlay);

                // Store the generated waveform in state so handleTimeUpdate uses the correct waveform
                if (waveform) {
                    dispatch({ type: actionTypes.SET_WAVEFORM_DATA, payload: waveform });
                    // Draw waveform AFTER audioBufferDuration is set, using bufferDuration for correct progress calculation
                    drawStaticWaveform(waveform, pausedPositionRef.current, bufferDuration, 0);
                }

                // Use audio buffer duration for playback timing calculations
                // This ensures scrubber/waveform progress matches actual audio playback
                const currentDuration = bufferDuration;

                // Get paused position and clamp to valid range
                const pausedPosition = Math.min(
                    Math.max(0, pausedPositionRef.current),
                    currentDuration,
                );

                // Record start time adjusted for paused position BEFORE creating source
                // This ensures stopPlayback() can capture position even if called immediately after playback()
                const audioContextTime = audioContext.currentTime;
                const adjustedStartTime = audioContextTime - pausedPosition;
                playbackStartTimeRef.current = adjustedStartTime;

                // Create and connect the source
                const source = audioContext.createBufferSource();
                source.buffer = audioBuffer;
                playbackSourceRef.current = source;

                // Connect directly to destination (no visualizer for playback)
                source.connect(audioContext.destination);
                dispatch({
                    type: actionTypes.SET_PLAYBACK_START_TIME,
                    payload: adjustedStartTime,
                });
                dispatch({ type: actionTypes.SET_IS_PLAYING, payload: true });

                // Clean up when finished
                source.onended = () => {
                    // Only reset paused position if this source is still active
                    // If playbackSourceRef.current is null or different, stopPlayback() already handled it
                    if (playbackSourceRef.current === source) {
                        playbackSourceRef.current = null;
                        isPlaybackModeRef.current = false;
                        // Reset paused position and progress tracking when audio ends naturally
                        pausedPositionRef.current = 0;
                        playbackStartTimeRef.current = 0;
                        lastDrawnProgressRef.current = -1;
                        dispatch({ type: actionTypes.SET_IS_PLAYING, payload: false });
                    }
                    // Don't close shared context - it may be reused
                };

                // Start playback from paused position (or 0 if never paused)
                source.start(0, pausedPosition);
            } catch (err) {
                console.error('Playback error:', err);
                dispatch({
                    type: actionTypes.SET_ERROR,
                    payload: err && err.message ? err.message : String(err),
                });
            }
        },
        [
            audioBufferDuration,
            audioDuration,
            drawStaticWaveform,
            generateStaticWaveform,
            getOrCreateContext,
            targetRate,
        ],
    );

    const stopPlayback = useCallback(() => {
        // Early return if not in playback mode - don't do anything during recording
        if (!isPlaybackModeRef.current && !playbackSourceRef.current) {
            return;
        }

        // Capture paused position BEFORE stopping the source
        // This ensures we capture the position even if source is stopped
        // Allow playbackStartTimeRef to be 0 (can happen if audioContext.currentTime was 0 when playback started)
        // As long as we have a source and context, we can calculate position
        if (
            playbackSourceRef.current &&
            sharedContextRef.current &&
            playbackStartTimeRef.current !== null &&
            playbackStartTimeRef.current !== undefined &&
            isPlaybackModeRef.current
        ) {
            const audioContext = sharedContextRef.current;
            const currentPosition = Math.max(
                0,
                audioContext.currentTime - playbackStartTimeRef.current,
            );
            // Use audioBufferDuration for playback timing (actual audio length)
            const playbackDuration = audioBufferDuration || audioDuration;
            // Only capture paused position if we're not at the end
            // If we're at or past the end, reset to 0 instead
            if (currentPosition >= playbackDuration && playbackDuration > 0) {
                pausedPositionRef.current = 0;
            } else {
                // Clamp to valid range and store paused position
                pausedPositionRef.current = Math.max(
                    0,
                    Math.min(currentPosition, playbackDuration),
                );
            }
        }
        // If playbackStartTimeRef is 0 but pausedPositionRef > 0, preserve it
        // This handles the case where playback() stops ongoing playback before setting playbackStartTimeRef
        // In this case, we keep the existing paused position (it was already set correctly)

        if (playbackSourceRef.current) {
            try {
                playbackSourceRef.current.stop();
            } catch (err) {
                // Ignore errors if already stopped
            }
            playbackSourceRef.current = null;
        }

        // Only reset playback mode and playing state if we were actually in playback mode
        if (isPlaybackModeRef.current) {
            isPlaybackModeRef.current = false;
            dispatch({ type: actionTypes.SET_IS_PLAYING, payload: false });
        }

        // Don't close shared context - it may be reused
    }, [audioBufferDuration, audioDuration]);

    /**
     * Seek to a specific time position in the audio
     * @param {number} targetTime - Target time in seconds
     * @param {boolean} resumePlayback - Whether to resume playback after seeking (defaults to current playing state)
     */
    const seek = useCallback(
        async (targetTime, resumePlayback = null) => {
            // Use audioBufferDuration for seeking (actual audio length)
            const playbackDuration = audioBufferDuration || audioDuration;
            // Clamp target time to valid range
            const clampedTime = Math.max(0, Math.min(targetTime, playbackDuration));

            // Determine if we should resume playback
            // If resumePlayback is explicitly provided, use it
            // Otherwise, resume if currently playing
            const shouldResume = resumePlayback !== null ? resumePlayback : isPlaying;

            // Stop current playback and capture position
            if (playbackSourceRef.current) {
                stopPlayback();
            }

            // Update paused position to target time
            pausedPositionRef.current = clampedTime;

            // Update playback start time to reflect the seek
            // This ensures time tracking is correct
            if (sharedContextRef.current && clampedTime > 0) {
                const audioContext = sharedContextRef.current;
                playbackStartTimeRef.current = audioContext.currentTime - clampedTime;
                dispatch({
                    type: actionTypes.SET_PLAYBACK_START_TIME,
                    payload: playbackStartTimeRef.current,
                });
            }

            // Redraw waveform at new position
            if (waveformData) {
                // Use default padding (0) for seek since AudioVisualizer will handle padding
                // Use playbackDuration for waveform drawing to match actual audio playback
                const playbackDuration = audioBufferDuration || audioDuration;
                drawStaticWaveform(waveformData, clampedTime, playbackDuration, 0);
            }

            // Resume playback if it was playing before seek
            // Set playback mode before resuming to ensure playback() works correctly
            if (shouldResume) {
                isPlaybackModeRef.current = true;
                await playback();
            }
        },
        [
            audioBufferDuration,
            audioDuration,
            drawStaticWaveform,
            isPlaying,
            playback,
            stopPlayback,
            waveformData,
        ],
    );

    /**
     * Handle time updates from AudioVisualizer during playback
     * Redraws the waveform with progress indication
     * Optimized: Only redraws when progress crosses a bar boundary
     */
    const handleTimeUpdate = useCallback(
        (currentTime, totalDuration, horizontalPadding = 0) => {
            if (waveformData && waveformData.length > 0) {
                // Use audioBufferDuration if available (set by playback with actual audioBuffer.duration)
                // This is the most accurate duration as it matches what Web Audio API uses
                // If not available, calculate from batches as fallback
                let playbackDuration;
                if (audioBufferDuration > 0) {
                    playbackDuration = audioBufferDuration;
                } else {
                    const batches = audioBatchesRef.current;
                    if (batches && batches.length > 0) {
                        playbackDuration = getTotalSampleCount(batches) / targetRate;
                    } else {
                        playbackDuration = totalDuration;
                    }
                }

                // Only draw if we have a valid duration
                if (playbackDuration > 0) {
                    // Calculate which bar the playhead is currently on
                    const progress = currentTime / playbackDuration;
                    const currentBar = Math.floor(progress * waveformData.length);

                    // Skip redraw if we're still on the same bar (optimization)
                    // This reduces redraws from ~60/sec to only when progress crosses bar boundaries
                    if (currentBar === lastDrawnProgressRef.current) {
                        return;
                    }
                    lastDrawnProgressRef.current = currentBar;

                    drawStaticWaveform(
                        waveformData,
                        currentTime,
                        playbackDuration,
                        horizontalPadding,
                    );
                }
            }
        },
        [audioBufferDuration, drawStaticWaveform, targetRate, waveformData],
    );

    /**
     * Get compressed audio from recorded batches
     * Converts PCM16 batches to M4A or WebM format depending on browser support
     * @returns {Promise<{ base64: string, format: string, blob: Blob } | null>}
     */
    const getCompressedAudio = useCallback(async () => {
        return convertToCompressedAudio(audioBatchesRef.current, targetRate);
    }, [targetRate]);

    return {
        audioBufferDuration,
        audioDuration,
        bindCanvas,
        error,
        getCompressedAudio,
        handleTimeUpdate,
        initializeWaveform,
        isPlaying,
        level,
        playback,
        playbackContext: sharedContextRef.current, // expose shared context for time tracking
        playbackStartTime,
        resume,
        seek,
        start,
        status,
        stop,
        stopPlayback,
        supportedAudioFormat: getBestAudioFormat(), // detected format: { mimeType, extension, format } or null
        waveformData,
    };
};

useAudioEvents.displayName = 'useAudioEvents';
export default useAudioEvents;
